/**
 * SAMPLE DATA for the token-wallet preview — none of this is real.
 *
 * This preview uses fixtures instead of the connected wallet, catalogue,
 * gateway and ledger. The UI labels these frames «نمونه». Nothing in this file
 * may be shown without that marker, and nothing here may be used as an
 * authoritative amount.
 *
 * Privacy (module rule «مدیر تننت متن گفتگوها را نمی‌بیند، فقط مصرف را»):
 * the `reference` of a usage row is only the KIND of reference («گفتگو»),
 * never a conversation title — the wallet history is visible to tenant admins.
 *
 * Money is an integer-Rial digit string (the repo's money contract); token
 * counts are plain integers. Dates are pre-formatted Jalali display strings
 * (display only — no date arithmetic is done on them anywhere).
 *
 * NOTE: keep imports out of this file (the unit test loads it directly).
 */

export interface TokenPackage {
  readonly id: string;
  readonly name: string;
  readonly tokens: number;
  /** Final payable integer Rial amount in the preview. */
  readonly priceRial: string;
  /** Optional ribbon text on the card (sample marketing copy). */
  readonly badge?: string;
}

/** Approved pack prices are final; the preview adds no extra tax. */
export const SAMPLE_VAT_BASIS_POINTS = 0;

export const SAMPLE_PACKAGES: readonly TokenPackage[] = [
  { id: 'base', name: 'پایه', tokens: 2_000_000, priceRial: '20000000' },
  { id: 'pro', name: 'حرفه‌ای', tokens: 5_000_000, priceRial: '49000000', badge: 'پیشنهادی' },
  { id: 'enterprise', name: 'سازمانی', tokens: 10_000_000, priceRial: '98000000' },
];

/** Default low-balance warning threshold (tokens). Sample. */
export const SAMPLE_WARNING_THRESHOLD = 20_000;

/** Jalali display date of the purchase the current balance belongs to. Sample. */
export const SAMPLE_CURRENT_PURCHASE_DATE = '۱۴۰۵/۰۵/۱۲';

export type HistoryKind = 'purchase' | 'usage' | 'refund' | 'gift';
export type HistoryStatus = 'done' | 'failed' | 'cancelled' | 'pending';

export interface WalletHistoryRow {
  readonly id: string;
  /** Pre-formatted Jalali display string. */
  readonly date: string;
  readonly kind: HistoryKind;
  /** Signed token change; 0 for a purchase attempt that did not complete. */
  readonly delta: number;
  readonly user: string;
  /** Kind of reference only («گفتگو» / «خرید بستهٔ …») — never a conversation title. */
  readonly reference: string;
  /** Invoice number — rendered LTR/mono when present. */
  readonly invoiceNo?: string;
  readonly status: HistoryStatus;
  /** Balance after this row (derived from the deltas, oldest first). */
  readonly balanceAfter: number;
}

type RawHistoryRow = Omit<WalletHistoryRow, 'balanceAfter'>;

/** Oldest first; `balanceAfter` is derived below so the ledger always adds up. */
const RAW_HISTORY_OLDEST_FIRST: readonly RawHistoryRow[] = [
  {
    id: 'h1',
    date: '۱۴۰۵/۰۵/۱۲',
    kind: 'purchase',
    delta: 100_000,
    user: 'علی رستمی',
    reference: 'خرید بستهٔ پایه',
    invoiceNo: 'INV-TK-۰۰۴۲',
    status: 'done',
  },
  {
    id: 'h2',
    date: '۱۴۰۵/۰۵/۲۰',
    kind: 'usage',
    delta: -18_400,
    user: 'علی رستمی',
    reference: 'گفتگو',
    status: 'done',
  },
  {
    id: 'h3',
    date: '۱۴۰۵/۰۶/۰۲',
    kind: 'usage',
    delta: -27_900,
    user: 'مریم صادقی',
    reference: 'گفتگو',
    status: 'done',
  },
  {
    id: 'h4',
    date: '۱۴۰۵/۰۶/۰۹',
    kind: 'usage',
    delta: -21_820,
    user: 'رضا مرادی',
    reference: 'گفتگو',
    status: 'done',
  },
  {
    id: 'h5',
    date: '۱۴۰۵/۰۶/۱۲',
    kind: 'usage',
    delta: -14_000,
    user: 'مریم صادقی',
    reference: 'گفتگو',
    status: 'done',
  },
  {
    id: 'h6',
    date: '۱۴۰۵/۰۶/۱۵',
    kind: 'gift',
    delta: 5_000,
    user: 'پشتیبانی ساختار',
    reference: 'اعتبار راه‌اندازی',
    status: 'done',
  },
  {
    id: 'h7',
    date: '۱۴۰۵/۰۶/۲۰',
    kind: 'purchase',
    delta: 0,
    user: 'علی رستمی',
    reference: 'خرید بستهٔ حرفه‌ای — پرداخت ناموفق',
    status: 'failed',
  },
  {
    id: 'h8',
    date: '۱۴۰۵/۰۶/۲۸',
    kind: 'refund',
    delta: 4_500,
    user: 'سامانه',
    reference: 'تحلیل ناموفق — بازگشت خودکار',
    status: 'done',
  },
  {
    id: 'h9',
    date: '۱۴۰۵/۰۷/۰۱',
    kind: 'usage',
    delta: -840,
    user: 'مریم صادقی',
    reference: 'گفتگو',
    status: 'done',
  },
  {
    id: 'h10',
    date: '۱۴۰۵/۰۷/۰۲',
    kind: 'usage',
    delta: -2_460,
    user: 'رضا مرادی',
    reference: 'گفتگو',
    status: 'done',
  },
  {
    id: 'h11',
    date: '۱۴۰۵/۰۷/۰۲',
    kind: 'usage',
    delta: -1_080,
    user: 'رضا مرادی',
    reference: 'گفتگو',
    status: 'done',
  },
];

function withRunningBalance(rows: readonly RawHistoryRow[]): readonly WalletHistoryRow[] {
  let balance = 0;
  return rows.map((row) => {
    balance += row.delta;
    return { ...row, balanceAfter: balance };
  });
}

/** Newest first — the order the table shows. Ends at the module's sample balance. */
export const SAMPLE_HISTORY: readonly WalletHistoryRow[] = [
  ...withRunningBalance(RAW_HISTORY_OLDEST_FIRST),
].reverse();

/** Tokens spent per day over the last 30 days (day 1 … 30). Sample. */
export const SAMPLE_DAILY_USAGE: readonly number[] = [
  3100, 2400, 0, 5200, 4100, 1800, 2600, 0, 0, 6400, 3800, 2900, 1500, 4700, 5100, 3300, 2100, 6900,
  4400, 3700, 0, 2800, 5600, 4900, 7200, 3100, 2400, 5800, 4200, 3600,
];
